import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import ProviderLayout from "@/layouts/ProviderLayout";
import { useToast } from "@/hooks/use-toast";
import {
  useInviteGroupProvider,
  useListGroupInvitations,
  useDeleteGroupInvitation,
  useUpdateGroupInvitation,
  useGetMyInvitations,
  useAcceptInvitation,
  useRejectInvitation,
  useLeaveGroup,
  useGetProviderAppointments,
  useGetCodeTypes,
  useGetProvider,
  useGetLocalities,
} from "@/lib/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, Pencil, Users, Mail, FileDigit, Percent, UserPlus, Clock, CheckCircle2, Search, Plus, Building2, MapPin, Sliders, X, ChevronDown, ChevronUp, RotateCcw, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { motion, Variants } from "framer-motion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";



export default function GroupTeam() {
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  
  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) {
      try { setUser(JSON.parse(u)); } catch(e) {}
    }
  }, []);

  const inviteMutation = useInviteGroupProvider();
  const deleteMutation = useDeleteGroupInvitation();
  const acceptMutation = useAcceptInvitation();
  const rejectMutation = useRejectInvitation();
  const leaveGroupMutation = useLeaveGroup();
  
  const { data: invitations, isLoading, refetch } = useListGroupInvitations();
  const { data: myInvitations, isLoading: isMyInvLoading } = useGetMyInvitations(user?.id, { enabled: !!user });
  
  const { data: appointmentsData } = useGetProviderAppointments({ page: 1, limit: 100 });
  const allAppointments = appointmentsData?.items || [];
  
  // Calculate real metrics from appointments
  const completedAppointments = allAppointments.filter((a: any) => a.status === 'COMPLETED');
  const realTotalRevenue = completedAppointments.reduce((sum: number, a: any) => sum + (Number(a.amount) || 0), 0);
  const realTotalPatients = new Set(allAppointments.map((a: any) => a.memberId)).size;
  
  const realPatientsList = allAppointments.map((a: any) => ({
    id: a.id,
    name: a.member?.user?.displayName || (a.member?.user?.firstName ? `${a.member.user.firstName} ${a.member.user.lastName}` : "Unknown Patient"),
    date: new Date(a.createdAt).toISOString().split('T')[0],
    service: a.bundle?.name || (a.notes?.startsWith('[ESTIMATE:') ? a.notes.match(/\[ESTIMATE:([^:\]]+)/)?.[1] : "General Consultation"),
    revenue: a.amount ? `$${a.amount}` : "$0",
    status: a.status
  })).slice(0, 5);

  const isClinic = user?.provider?.practiceType === "group" || localStorage.getItem("practiceType") === "group";

  const [activeTab, setActiveTab] = useState<'team' | 'groups'>('team');
  const [serverError, setServerError] = useState<string | null>(null);
  const [invitationSearch, setInvitationSearch] = useState("");
  const [invitationStatus, setInvitationStatus] = useState("ALL");

  const updateMutation = useUpdateGroupInvitation();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editTarget, setEditTarget] = useState<any>(null);
  const [editPercentage, setEditPercentage] = useState("");
  const [editCategoryRates, setEditCategoryRates] = useState<Record<string, number | string>>({});
  const [editSelectedCodeTypes, setEditSelectedCodeTypes] = useState<Record<string, boolean>>({});
  const [assignedLocationIds, setAssignedLocationIds] = useState<Record<string, boolean>>({});
  const [editAssignedLocationIds, setEditAssignedLocationIds] = useState<Record<string, boolean>>({});
  const [locationRates, setLocationRates] = useState<Record<string, Record<string, number | string>>>({});
  const [activeEditLocationId, setActiveEditLocationId] = useState<string | null>(null);
  const [editLocationRates, setEditLocationRates] = useState<Record<string, Record<string, number | string>>>({});
  const [editActiveLocationId, setEditActiveLocationId] = useState<string | null>(null);

  const handleUpdateLocationRate = (locId: string, codeTypeName: string, rate: number | string) => {
    setLocationRates(prev => ({
      ...prev,
      [locId]: {
        ...(prev[locId] || {}),
        [codeTypeName]: rate,
      }
    }));
  };

  const handleRemoveLocationRate = (locId: string, codeTypeName: string) => {
    setLocationRates(prev => {
      const copy = { ...(prev[locId] || {}) };
      delete copy[codeTypeName];
      if (Object.keys(copy).length === 0) {
        const next = { ...prev };
        delete next[locId];
        return next;
      }
      return { ...prev, [locId]: copy };
    });
  };

  const handleResetLocationRates = (locId: string) => {
    setLocationRates(prev => {
      const next = { ...prev };
      delete next[locId];
      return next;
    });
  };

  const handleUpdateEditLocationRate = (locId: string, codeTypeName: string, rate: number | string) => {
    setEditLocationRates(prev => ({
      ...prev,
      [locId]: {
        ...(prev[locId] || {}),
        [codeTypeName]: rate,
      }
    }));
  };

  const handleRemoveEditLocationRate = (locId: string, codeTypeName: string) => {
    setEditLocationRates(prev => {
      const copy = { ...(prev[locId] || {}) };
      delete copy[codeTypeName];
      if (Object.keys(copy).length === 0) {
        const next = { ...prev };
        delete next[locId];
        return next;
      }
      return { ...prev, [locId]: copy };
    });
  };

  const handleResetEditLocationRates = (locId: string) => {
    setEditLocationRates(prev => {
      const next = { ...prev };
      delete next[locId];
      return next;
    });
  };

  const { data: currentProviderData } = useGetProvider(user?.id || "", { query: { enabled: !!user?.id } });

  const { data: localitiesData = [] } = useGetLocalities();

  const providerLocations = useMemo(() => {
    const prov = currentProviderData || user?.provider || user;
    const rawAddresses = prov?.addresses || prov?.user?.addresses || prov?.locations || user?.addresses || user?.provider?.addresses || [];
    
    const locsList: Array<{ id: string; name: string; raw?: any }> = [];

    // 1. User/Provider registered addresses
    if (Array.isArray(rawAddresses) && rawAddresses.length > 0) {
      rawAddresses.forEach((addr: any, index: number) => {
        const id = String(addr.id || `loc_${index + 1}`);
        const city = addr.city ? addr.city.trim().toUpperCase() : "";
        const state = addr.state ? addr.state.trim().toUpperCase() : "";
        const cityState = city && state ? `${city}, ${state}` : (city || state || "");
        const rawName = addr.name || addr.label || addr.street1 || addr.address1 || "";
        const name = rawName && cityState ? `${rawName} (${cityState})` : (cityState || rawName || `Location ${index + 1}`);
        locsList.push({ id, name, raw: addr });
      });
    }

    // 2. User/Provider localities or master localities matching provider state
    if (locsList.length === 0 && Array.isArray(localitiesData) && localitiesData.length > 0) {
      const selectedLocalityKeys = new Set<string>();
      if (user?.addresses && Array.isArray(user.addresses)) {
        user.addresses.forEach((a: any) => {
          const ids = a.localityIds || (a.localityId ? [a.localityId] : []);
          if (Array.isArray(ids)) {
            ids.forEach((id: any) => { if (id) selectedLocalityKeys.add(String(id).trim()); });
          }
        });
      }
      if (selectedLocalityKeys.size === 0 && user?.localities && Array.isArray(user.localities)) {
        user.localities.forEach((l: any) => {
          if (typeof l === "string") selectedLocalityKeys.add(l.trim());
          else if (l && typeof l === "object") {
            if (l.id) selectedLocalityKeys.add(String(l.id).trim());
            if (l.name) selectedLocalityKeys.add(String(l.name).trim());
          }
        });
      }

      if (selectedLocalityKeys.size > 0) {
        localitiesData.forEach((loc: any) => {
          const locIdStr = String(loc.id).trim();
          const locName = String(loc.name || '').trim().toUpperCase();
          for (const key of Array.from(selectedLocalityKeys)) {
            if (locIdStr === key || locName.toLowerCase() === key.toLowerCase()) {
              locsList.push({ id: locIdStr, name: loc.name || locName, raw: loc });
              break;
            }
          }
        });
      }

      // Fallback to Chico, CA or first locality if available
      if (locsList.length === 0) {
        const chicoLoc = localitiesData.find((l: any) => l.name && l.name.toUpperCase().includes("CHICO"));
        if (chicoLoc) {
          locsList.push({ id: String(chicoLoc.id), name: chicoLoc.name, raw: chicoLoc });
        } else if (localitiesData[0]) {
          locsList.push({ id: String(localitiesData[0].id), name: localitiesData[0].name, raw: localitiesData[0] });
        }
      }
    }

    if (locsList.length === 0) {
      locsList.push(
        { id: "loc_1", name: "CHICO, CA" },
        { id: "loc_2", name: "Loc 2 (Secondary Location)" }
      );
    }

    return locsList;
  }, [currentProviderData, user, localitiesData]);

  useEffect(() => {
    if (providerLocations.length > 0) {
      const initialLocs: Record<string, boolean> = {};
      providerLocations.forEach((loc) => {
        initialLocs[loc.id] = true;
      });
      setAssignedLocationIds(initialLocs);
    }
  }, [providerLocations]);

  const handleSavePercentage = () => {
    if (!editTarget) return;
    
    const cleanedRates: Record<string, any> = {};
    const selectedNums: number[] = [];
    Object.entries(editCategoryRates).forEach(([key, val]) => {
      const isSelected = editSelectedCodeTypes[key] !== false;
      if (isSelected && val !== "" && val !== null && val !== undefined && !key.startsWith("loc_") && key !== "assignedLocationIds" && key !== "assignedLocations") {
        const n = Number(val);
        if (!isNaN(n)) {
          cleanedRates[key] = n;
          selectedNums.push(n);
        }
      }
    });

    if (providerLocations.length > 0) {
      const selectedLocObjs = providerLocations.filter(loc => editAssignedLocationIds[loc.id] !== false);
      cleanedRates.assignedLocationIds = selectedLocObjs.map(loc => loc.id);
      cleanedRates.assignedLocations = selectedLocObjs.map(loc => loc.name);

      selectedLocObjs.forEach((loc, idx) => {
        const locSpecific = editLocationRates[loc.id];
        if (locSpecific && typeof locSpecific === 'object') {
          const cleanedLocObj: Record<string, number> = {};
          Object.entries(locSpecific).forEach(([ctName, rVal]) => {
            if (rVal !== "" && rVal !== null && rVal !== undefined) {
              const num = Number(rVal);
              if (!isNaN(num)) cleanedLocObj[ctName] = num;
            }
          });
          if (Object.keys(cleanedLocObj).length > 0) {
            cleanedRates[loc.id] = cleanedLocObj;
            cleanedRates[`loc_${idx + 1}`] = cleanedLocObj;
          }
        }
      });
    }

    const avgPercentage = selectedNums.length > 0
      ? Math.round(selectedNums.reduce((a, b) => a + b, 0) / selectedNums.length)
      : Number(editPercentage) || groupDefaultMedicareCharge;

    updateMutation.mutate(
      {
        id: editTarget.id,
        percentage: avgPercentage,
        categoryRates: Object.keys(cleanedRates).length > 0 ? cleanedRates : undefined
      },
      {
        onSuccess: () => {
          toast({ title: "Updated", description: "Team member details updated successfully." });
          setShowEditDialog(false);
          setEditTarget(null);
          setEditLocationRates({});
          setEditActiveLocationId(null);
          refetch();
        },
        onError: (err: any) => {
          toast({ title: "Error", description: err.message, variant: "destructive" });
        }
      }
    );
  };

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const fadeUp: Variants = {
    hidden: { opacity: 0, y: 15 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.4, ease: "easeOut" },
    }),
  };

  const pendingCount = invitations?.data?.filter((inv: any) => inv.status === 'PENDING').length || 0;
  const acceptedCount = invitations?.data?.filter((inv: any) => inv.status === 'ACCEPTED').length || 0;
  const totalCount = invitations?.data?.length || 0;
  const pendingReceivedCount = myInvitations?.data?.filter((inv: any) => inv.status === 'PENDING').length || 0;

  const filteredMyInvitations = myInvitations?.data?.filter((inv: any) => {
    if (inv.status === 'LEFT') return false;
    const matchesStatus = invitationStatus === "ALL" || inv.status === invitationStatus;
    const groupName = inv.group?.user?.displayName || (inv.group?.user?.firstName ? `${inv.group.user.firstName} ${inv.group.user.lastName}` : "Group Practice");
    const matchesSearch = groupName.toLowerCase().includes(invitationSearch.toLowerCase());
    return matchesStatus && matchesSearch;
  }) || [];

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        toast({ title: "Removed", description: "Provider has been removed from your team." });
      },
      onError: (error: any) => {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      }
    });
  };

  const groupDefaultMedicareCharge = useMemo(() => {
    const prov = currentProviderData || user?.provider;
    const raw = prov?.medicareCharge;
    if (!raw || raw === "Standard Rate") return 50;
    const num = parseFloat(String(raw).replace(/%/g, ""));
    return !isNaN(num) ? num : 50;
  }, [currentProviderData, user]);

  const groupCategoryRates = useMemo<Record<string, any>>(() => {
    const prov = currentProviderData || user?.provider;
    return prov?.categoryRates || {};
  }, [currentProviderData, user]);

  const { data: codeTypesData = [] } = useGetCodeTypes();
  const [categoryRates, setCategoryRates] = useState<Record<string, number | string>>({});
  const [selectedCodeTypes, setSelectedCodeTypes] = useState<Record<string, boolean>>({});
  const [customCodeTypesList, setCustomCodeTypesList] = useState<string[]>([]);

  const [newCodeType, setNewCodeType] = useState<string>("");
  const [newCodeTypeRate, setNewCodeTypeRate] = useState<string>("");

  const [editNewCodeType, setEditNewCodeType] = useState<string>("");
  const [editNewCodeTypeRate, setEditNewCodeTypeRate] = useState<string>("");

  const [showAddCustomCode, setShowAddCustomCode] = useState(false);
  const [customCodeName, setCustomCodeName] = useState("");
  const [customCodeRate, setCustomCodeRate] = useState("");

  const [showEditAddCustomCode, setShowEditAddCustomCode] = useState(false);
  const [editCustomCodeName, setEditCustomCodeName] = useState("");
  const [editCustomCodeRate, setEditCustomCodeRate] = useState("");

  const handleAddCustomCode = () => {
    const trimmed = customCodeName.trim().toUpperCase();
    if (!trimmed) return;
    if (!customCodeTypesList.includes(trimmed)) {
      setCustomCodeTypesList(prev => [...prev, trimmed]);
    }
    const rateToUse = customCodeRate !== "" ? customCodeRate : groupDefaultMedicareCharge;
    setSelectedCodeTypes(prev => ({ ...prev, [trimmed]: true }));
    setCategoryRates(prev => ({ ...prev, [trimmed]: rateToUse }));
    setCustomCodeName("");
    setCustomCodeRate("");
    setShowAddCustomCode(false);
  };

  const handleEditAddCustomCode = () => {
    const trimmed = editCustomCodeName.trim().toUpperCase();
    if (!trimmed) return;
    if (!customCodeTypesList.includes(trimmed)) {
      setCustomCodeTypesList(prev => [...prev, trimmed]);
    }
    const rateToUse = editCustomCodeRate !== "" ? editCustomCodeRate : groupDefaultMedicareCharge;
    setEditSelectedCodeTypes(prev => ({ ...prev, [trimmed]: true }));
    setEditCategoryRates(prev => ({ ...prev, [trimmed]: rateToUse }));
    setEditCustomCodeName("");
    setEditCustomCodeRate("");
    setShowEditAddCustomCode(false);
  };

  const activeCodeTypes = useMemo(() => {
    if (codeTypesData && codeTypesData.length > 0) {
      return codeTypesData
        .filter((ct: any) => ct && ct.isActive !== false)
        .map((ct: any, idx: number) => {
          const name = typeof ct === "object" && ct !== null ? (ct.name || ct.code || `CodeType-${idx}`) : String(ct || `CodeType-${idx}`);
          const code = typeof ct === "object" && ct !== null ? (ct.code || ct.name || name) : name;
          const id = typeof ct === "object" && ct !== null && ct.id != null ? String(ct.id) : code;
          return { id, name, code };
        });
    }
    return [
      { id: "PFS", name: "PFS", code: "PFS" },
      { id: "CLFS", name: "CLFS", code: "CLFS" },
      { id: "HCPCS", name: "HCPCS", code: "HCPCS" },
    ];
  }, [codeTypesData]);

  const displayedCodeTypesGroup = useMemo(() => {
    const base = activeCodeTypes.map((ct: any) => ({
      id: ct.id || ct.name,
      name: ct.name || ct.code || ct.id,
    }));
    
    const extraFromCat = Object.keys(categoryRates || {}).filter(k => 
      typeof categoryRates[k] !== 'object' && 
      !k.startsWith('loc_') && 
      k !== 'assignedLocationIds' &&
      k !== 'assignedLocations' &&
      !base.some(b => b.name.toLowerCase() === k.toLowerCase() || String(b.id).toLowerCase() === k.toLowerCase())
    );
    const extraFromEdit = Object.keys(editCategoryRates || {}).filter(k => 
      typeof editCategoryRates[k] !== 'object' && 
      !k.startsWith('loc_') && 
      k !== 'assignedLocationIds' &&
      k !== 'assignedLocations' &&
      !base.some(b => b.name.toLowerCase() === k.toLowerCase() || String(b.id).toLowerCase() === k.toLowerCase())
    );

    const allExtras = Array.from(new Set([...customCodeTypesList, ...extraFromCat, ...extraFromEdit]));
    allExtras.forEach(eName => {
      if (!base.some(b => b.name.toLowerCase() === eName.toLowerCase())) {
        base.push({ id: eName, name: eName });
      }
    });

    return base;
  }, [activeCodeTypes, customCodeTypesList, categoryRates, editCategoryRates]);

  // Pre-fill category rates and selection with provider default values
  useEffect(() => {
    if (activeCodeTypes && activeCodeTypes.length > 0) {
      const initialRates: Record<string, number | string> = {};
      const initialSelected: Record<string, boolean> = {};

      const provCategoryRatesKeys = Object.keys(groupCategoryRates || {}).filter(
        k => !k.startsWith('loc_') && k !== 'assignedLocationIds' && k !== 'assignedLocations'
      );
      const provCodeTypeNames = Array.isArray((currentProviderData || user?.provider)?.codeTypes)
        ? (currentProviderData || user?.provider).codeTypes.map((ct: any) => typeof ct === 'object' ? (ct.name || ct.code) : String(ct))
        : [];
      const hasProviderDefinedCodeTypes = provCategoryRatesKeys.length > 0 || provCodeTypeNames.length > 0;

      activeCodeTypes.forEach((codeTypeObj: any) => {
        const codeTypeName = codeTypeObj.name;
        const codeTypeId = codeTypeObj.id;
        const defaultForType = groupCategoryRates[codeTypeName] ?? groupCategoryRates[codeTypeId] ?? groupDefaultMedicareCharge;
        initialRates[codeTypeName] = defaultForType;
        initialRates[codeTypeId] = defaultForType;

        if (hasProviderDefinedCodeTypes) {
          const isDefinedByProv = provCategoryRatesKeys.some(k => k.toLowerCase() === codeTypeName.toLowerCase() || k.toLowerCase() === String(codeTypeId).toLowerCase()) ||
            provCodeTypeNames.some((n: string) => n.toLowerCase() === codeTypeName.toLowerCase() || n.toLowerCase() === String(codeTypeId).toLowerCase());
          initialSelected[codeTypeName] = isDefinedByProv;
          initialSelected[codeTypeId] = isDefinedByProv;
        } else {
          const isDefaultMain = codeTypeName.toUpperCase() === "CPT" || codeTypeName.toUpperCase() === "CLFS";
          initialSelected[codeTypeName] = isDefaultMain;
          initialSelected[codeTypeId] = isDefaultMain;
        }
      });
      setCategoryRates(initialRates);
      setSelectedCodeTypes(initialSelected);
    }
  }, [activeCodeTypes, groupCategoryRates, groupDefaultMedicareCharge, currentProviderData, user]);

  const resetFormCategoryRates = () => {
    if (activeCodeTypes && activeCodeTypes.length > 0) {
      const initialRates: Record<string, number | string> = {};
      const initialSelected: Record<string, boolean> = {};

      const provCategoryRatesKeys = Object.keys(groupCategoryRates || {}).filter(
        k => !k.startsWith('loc_') && k !== 'assignedLocationIds' && k !== 'assignedLocations'
      );
      const provCodeTypeNames = Array.isArray((currentProviderData || user?.provider)?.codeTypes)
        ? (currentProviderData || user?.provider).codeTypes.map((ct: any) => typeof ct === 'object' ? (ct.name || ct.code) : String(ct))
        : [];
      const hasProviderDefinedCodeTypes = provCategoryRatesKeys.length > 0 || provCodeTypeNames.length > 0;

      activeCodeTypes.forEach((codeTypeObj: any) => {
        const codeTypeName = codeTypeObj.name;
        const codeTypeId = codeTypeObj.id;
        const defaultForType = groupCategoryRates[codeTypeName] ?? groupCategoryRates[codeTypeId] ?? groupDefaultMedicareCharge;
        initialRates[codeTypeName] = defaultForType;
        initialRates[codeTypeId] = defaultForType;

        if (hasProviderDefinedCodeTypes) {
          const isDefinedByProv = provCategoryRatesKeys.some(k => k.toLowerCase() === codeTypeName.toLowerCase() || k.toLowerCase() === String(codeTypeId).toLowerCase()) ||
            provCodeTypeNames.some((n: string) => n.toLowerCase() === codeTypeName.toLowerCase() || n.toLowerCase() === String(codeTypeId).toLowerCase());
          initialSelected[codeTypeName] = isDefinedByProv;
          initialSelected[codeTypeId] = isDefinedByProv;
        } else {
          const isDefaultMain = codeTypeName.toUpperCase() === "CPT" || codeTypeName.toUpperCase() === "CLFS";
          initialSelected[codeTypeName] = isDefaultMain;
          initialSelected[codeTypeId] = isDefaultMain;
        }
      });
      setCategoryRates(initialRates);
      setSelectedCodeTypes(initialSelected);
    }
    if (providerLocations.length > 0) {
      const initialLocs: Record<string, boolean> = {};
      providerLocations.forEach((loc) => {
        initialLocs[loc.id] = true;
      });
      setAssignedLocationIds(initialLocs);
    }
    setLocationRates({});
    setActiveEditLocationId(null);
    setShowAddCustomCode(false);
    setCustomCodeName("");
    setCustomCodeRate("");
  };

  const onSubmit = (data: any) => {
    setServerError(null);

    const cleanedRates: Record<string, any> = {};
    const selectedNums: number[] = [];
    Object.entries(categoryRates).forEach(([key, val]) => {
      const isSelected = selectedCodeTypes[key] !== false;
      if (isSelected && val !== "" && val !== null && val !== undefined && !key.startsWith("loc_") && key !== "assignedLocationIds" && key !== "assignedLocations") {
        const num = Number(val);
        if (!isNaN(num)) {
          cleanedRates[key] = num;
          selectedNums.push(num);
        }
      }
    });

    if (providerLocations.length > 0) {
      const selectedLocObjs = providerLocations.filter(loc => assignedLocationIds[loc.id] !== false);
      cleanedRates.assignedLocationIds = selectedLocObjs.map(loc => loc.id);
      cleanedRates.assignedLocations = selectedLocObjs.map(loc => loc.name);

      selectedLocObjs.forEach((loc, idx) => {
        const locSpecific = locationRates[loc.id];
        if (locSpecific && typeof locSpecific === 'object') {
          const cleanedLocObj: Record<string, number> = {};
          Object.entries(locSpecific).forEach(([ctName, rVal]) => {
            if (rVal !== "" && rVal !== null && rVal !== undefined) {
              const num = Number(rVal);
              if (!isNaN(num)) cleanedLocObj[ctName] = num;
            }
          });
          if (Object.keys(cleanedLocObj).length > 0) {
            cleanedRates[loc.id] = cleanedLocObj;
            cleanedRates[`loc_${idx + 1}`] = cleanedLocObj;
          }
        }
      });
    }

    const avgPercentage = selectedNums.length > 0
      ? Math.round(selectedNums.reduce((a, b) => a + b, 0) / selectedNums.length)
      : Number(data.percentage) || groupDefaultMedicareCharge;

    inviteMutation.mutate(
      {
        email: data.email,
        npiNumber: data.npiNumber,
        percentage: avgPercentage,
        categoryRates: Object.keys(cleanedRates).length > 0 ? cleanedRates : undefined
      },
      {
        onSuccess: (res: any) => {
          toast({
            title: "Invitation Sent",
            description: `An invitation has been sent to ${data.email}.`,
          });
          reset();
          resetFormCategoryRates();
          refetch();
        },
        onError: (error: any) => {
          setServerError(error.message);
        }
      }
    );
  };

  return (
    <ProviderLayout>
      <div className="w-full space-y-8">
        <motion.div custom={0} variants={fadeUp} initial="hidden" animate="visible" className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-xl flex-shrink-0">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">My Team & Groups</h1>
              <p className="text-muted-foreground text-sm mt-0.5">
                Invite providers to build your practice team, and manage your memberships in external medical groups.
              </p>
            </div>
          </div>

          {/* View Mode Switcher */}
          <div className="flex items-center gap-1.5 bg-muted/80 p-1.5 rounded-2xl border border-border shrink-0 self-start lg:self-auto max-w-full overflow-x-auto">
            <button
              type="button"
              onClick={() => setActiveTab('team')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap shrink-0 ${
                activeTab === 'team'
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Users className="w-4 h-4 shrink-0" />
              <span>My Team (Practice Team)</span>
              {totalCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] shrink-0">
                  {acceptedCount}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('groups')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap shrink-0 ${
                activeTab === 'groups'
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Building2 className="w-4 h-4 shrink-0" />
              <span>My Group (Joined Groups)</span>
              {pendingReceivedCount > 0 ? (
                <span className="ml-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-extrabold animate-pulse shrink-0">
                  {pendingReceivedCount} New
                </span>
              ) : null}
            </button>
          </div>
        </motion.div>

        {/* Stats Section */}
        {activeTab === 'team' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {[
              { label: "Total Members", value: isLoading ? "—" : totalCount, icon: Users, color: "text-blue-600", bg: "bg-blue-50 dark:bg-blue-900/20" },
              { label: "Active Providers", value: isLoading ? "—" : acceptedCount, icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50 dark:bg-green-900/20" },
              { label: "Pending Invites", value: isLoading ? "—" : pendingCount, icon: Clock, color: "text-amber-600", bg: "bg-amber-50 dark:bg-amber-900/20" },
            ].map((stat, i) => (
              <motion.div key={stat.label} custom={i + 1} variants={fadeUp} initial="hidden" animate="visible">
                <div className="bg-card rounded-2xl border border-border p-5 shadow-xs hover:shadow-sm transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center`}>
                      <stat.icon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                  </div>
                  {isLoading ? <Skeleton className="h-7 w-12 mb-1" /> : <p className="text-2xl font-bold">{stat.value}</p>}
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Invite Form */}
          {activeTab === 'team' && (
            <motion.div custom={2} variants={fadeUp} initial="hidden" animate="visible" className="lg:col-span-5 xl:col-span-4">
              <Card className="rounded-2xl shadow-xs border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-primary" />
                    Invite Provider
                  </CardTitle>
                  <CardDescription>Add a new provider to your practice.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="w-4 h-4" /> Email Address
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        className="rounded-xl bg-muted/30 focus:bg-background transition-colors"
                        placeholder="provider@example.com"
                        {...register("email", { required: "Email is required" })}
                      />
                      {errors.email && <p className="text-sm text-red-500">{String(errors.email.message)}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="npiNumber" className="flex items-center gap-2 text-muted-foreground">
                        <FileDigit className="w-4 h-4" /> NPI Number
                      </Label>
                      <Input
                        id="npiNumber"
                        className="rounded-xl bg-muted/30 focus:bg-background transition-colors"
                        placeholder="1234567890"
                        {...register("npiNumber", { required: "NPI Number is required" })}
                      />
                      {errors.npiNumber && <p className="text-sm text-red-500">{String(errors.npiNumber.message)}</p>}
                    </div>

                    {/* Code Type % Rates */}
                    <div className="space-y-2.5 pt-2">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2 text-foreground font-semibold text-xs uppercase tracking-wider">
                          <Percent className="w-3.5 h-3.5 text-primary" /> Billing Code Types & Rates
                        </Label>
                      </div>
                      <p className="text-[11px] text-muted-foreground">Select applicable billing code types and set reimbursement % rates.</p>

                      {/* Clean Code Types Table */}
                      <div className="rounded-xl border border-border/80 bg-card overflow-hidden divide-y divide-border/60">
                        <div className="flex items-center justify-between px-3 py-2 bg-muted/40 text-[11px] font-semibold text-muted-foreground">
                          <span>Code Type</span>
                          <span>Reimbursement Rate</span>
                        </div>

                        {displayedCodeTypesGroup.map((codeTypeObj: any, idx: number) => {
                          const codeTypeName = typeof codeTypeObj === "object" && codeTypeObj !== null
                            ? (codeTypeObj.name || codeTypeObj.code || String(codeTypeObj.id || idx))
                            : String(codeTypeObj || `CodeType-${idx}`);
                          const codeTypeId = codeTypeObj?.id || codeTypeName;
                          const isSelected = selectedCodeTypes[codeTypeName] === true || selectedCodeTypes[codeTypeId] === true;
                          const currentVal = categoryRates[codeTypeName] ?? categoryRates[codeTypeId] ?? groupDefaultMedicareCharge;
                          const defaultForType = groupCategoryRates[codeTypeName] ?? groupCategoryRates[codeTypeId] ?? groupDefaultMedicareCharge;
                          const isCustomCode = customCodeTypesList.includes(codeTypeName);

                          return (
                            <div
                              key={codeTypeId}
                              className={`flex items-center justify-between gap-3 px-3 py-2 transition-colors ${
                                isSelected ? "bg-card" : "bg-muted/10 opacity-60"
                              }`}
                            >
                              <div className="flex items-center gap-2.5 min-w-0">
                                <Checkbox
                                  id={`invite-code-${codeTypeId}`}
                                  checked={isSelected}
                                  onCheckedChange={(checked) => {
                                    const isChecked = Boolean(checked);
                                    setSelectedCodeTypes(prev => ({
                                      ...prev,
                                      [codeTypeName]: isChecked,
                                      [codeTypeId]: isChecked,
                                    }));
                                    if (isChecked && (categoryRates[codeTypeName] === undefined || categoryRates[codeTypeName] === "")) {
                                      setCategoryRates(prev => ({
                                        ...prev,
                                        [codeTypeName]: defaultForType,
                                        [codeTypeId]: defaultForType,
                                      }));
                                    }
                                  }}
                                />
                                <label
                                  htmlFor={`invite-code-${codeTypeId}`}
                                  className="flex items-center gap-1.5 cursor-pointer select-none"
                                >
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold transition-colors ${
                                    isSelected
                                      ? "bg-primary/10 text-primary border border-primary/25"
                                      : "bg-muted text-muted-foreground border border-border"
                                  }`}>
                                    {String(codeTypeName)}
                                  </span>
                                </label>
                                {isCustomCode && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCustomCodeTypesList(prev => prev.filter(c => c !== codeTypeName));
                                      setSelectedCodeTypes(prev => ({ ...prev, [codeTypeName]: false, [codeTypeId]: false }));
                                    }}
                                    className="text-muted-foreground/50 hover:text-red-500 text-xs transition-colors p-0.5"
                                    title="Remove custom code type"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                )}
                              </div>

                              <div className="flex items-center gap-2">
                                <div className="relative w-24">
                                  <Input
                                    type="number"
                                    min={0}
                                    max={100}
                                    disabled={!isSelected}
                                    placeholder={`${defaultForType}`}
                                    value={isSelected ? currentVal : ""}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setCategoryRates(prev => ({
                                        ...prev,
                                        [codeTypeName]: val,
                                        [codeTypeId]: val
                                      }));
                                    }}
                                    className={`pr-6 h-8 text-xs font-semibold text-right rounded-lg transition-all ${
                                      isSelected
                                        ? "bg-background border-border font-bold shadow-2xs"
                                        : "bg-muted/40 text-muted-foreground border-transparent cursor-not-allowed"
                                    }`}
                                  />
                                  <span className={`absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-semibold ${
                                    isSelected ? "text-muted-foreground" : "text-muted-foreground/40"
                                  }`}>%</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Subtle Add Custom Code Action */}
                      {!showAddCustomCode ? (
                        <button
                          type="button"
                          onClick={() => setShowAddCustomCode(true)}
                          className="text-xs text-primary hover:underline font-medium flex items-center gap-1 pt-0.5"
                        >
                          <Plus className="w-3.5 h-3.5" /> Add custom code type
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 p-2 rounded-xl bg-muted/30 border border-dashed border-primary/30 mt-1">
                          <Input
                            placeholder="Code name (e.g. LAB)"
                            value={customCodeName}
                            onChange={(e) => setCustomCodeName(e.target.value.toUpperCase())}
                            className="h-8 text-xs bg-background uppercase font-bold"
                            autoFocus
                          />
                          <div className="relative w-20 shrink-0">
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              placeholder={`${groupDefaultMedicareCharge}`}
                              value={customCodeRate}
                              onChange={(e) => setCustomCodeRate(e.target.value)}
                              className="pr-6 h-8 text-xs font-semibold text-right bg-background"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs font-semibold">%</span>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            className="h-8 px-2.5 text-xs font-semibold shrink-0"
                            disabled={!customCodeName.trim()}
                            onClick={handleAddCustomCode}
                          >
                            Add
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs text-muted-foreground shrink-0"
                            onClick={() => {
                              setShowAddCustomCode(false);
                              setCustomCodeName("");
                              setCustomCodeRate("");
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Facility Locations Assignment */}
                    {providerLocations.length > 0 && (
                      <div className="space-y-2.5 pt-2">
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-2 text-foreground font-semibold text-xs uppercase tracking-wider">
                            <MapPin className="w-3.5 h-3.5 text-primary" /> Assigned Practice Locations
                          </Label>
                          {providerLocations.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const allAssigned = providerLocations.every(l => assignedLocationIds[l.id] !== false);
                                const next: Record<string, boolean> = {};
                                providerLocations.forEach(l => { next[l.id] = !allAssigned; });
                                setAssignedLocationIds(next);
                                if (allAssigned) setActiveEditLocationId(null);
                              }}
                              className="text-[11px] text-primary hover:underline font-medium"
                            >
                              {providerLocations.every(l => assignedLocationIds[l.id] !== false) ? "Deselect All" : "Select All"}
                            </button>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground">Select provider locations. Custom rates can optionally be configured per facility.</p>
                        
                        <div className="space-y-2">
                          {providerLocations.map((loc) => {
                            const isSelected = assignedLocationIds[loc.id] !== false;
                            const locCustomRates = locationRates[loc.id] || {};
                            const customRatesCount = Object.keys(locCustomRates).filter(k => locCustomRates[k] !== "" && locCustomRates[k] !== undefined && locCustomRates[k] !== null).length;
                            const hasCustomRates = customRatesCount > 0;
                            const isExpanded = activeEditLocationId === loc.id;

                            const currentFormCodeTypes = displayedCodeTypesGroup.filter(
                              (ct: any) => selectedCodeTypes[ct.name] === true || selectedCodeTypes[ct.id] === true
                            );

                            return (
                              <div
                                key={loc.id}
                                className={`border rounded-xl transition-all overflow-hidden ${
                                  isSelected
                                    ? "bg-card border-border/80 shadow-2xs"
                                    : "bg-muted/10 border-border/50 opacity-60"
                                }`}
                              >
                                <div className="flex items-center justify-between gap-3 p-2.5 px-3">
                                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                    <Checkbox
                                      id={`invite-loc-${loc.id}`}
                                      checked={isSelected}
                                      onCheckedChange={(checked) => {
                                        const val = Boolean(checked);
                                        setAssignedLocationIds(prev => ({
                                          ...prev,
                                          [loc.id]: val
                                        }));
                                        if (!val && activeEditLocationId === loc.id) {
                                          setActiveEditLocationId(null);
                                        }
                                      }}
                                    />
                                    <label htmlFor={`invite-loc-${loc.id}`} className="text-xs font-medium text-foreground cursor-pointer select-none truncate">
                                      {loc.name}
                                    </label>
                                  </div>

                                  {isSelected && (
                                    <div className="flex items-center gap-1.5 shrink-0">
                                      {hasCustomRates && (
                                        <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary border-primary/20 font-semibold flex items-center gap-1">
                                          <Sliders className="w-2.5 h-2.5" />
                                          <span>{customRatesCount} custom</span>
                                        </Badge>
                                      )}
                                      <button
                                        type="button"
                                        className={`text-[11px] font-medium px-2 py-1 rounded-md flex items-center gap-1 transition-colors ${
                                          isExpanded
                                            ? "bg-primary/10 text-primary font-semibold"
                                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                                        }`}
                                        title="Configure location-specific fee schedule rates"
                                        onClick={() => setActiveEditLocationId(prev => prev === loc.id ? null : loc.id)}
                                      >
                                        {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                        <span>{isExpanded ? "Close" : hasCustomRates ? "Edit rates" : "Custom rates"}</span>
                                      </button>
                                    </div>
                                  )}
                                </div>

                                {/* Location-specific Fee Schedule rates editor panel */}
                                {isSelected && isExpanded && (
                                  <div className="p-3 bg-muted/20 border-t border-border/70 space-y-2.5">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                                        <MapPin className="w-3.5 h-3.5 text-primary" />
                                        Custom Rates for this Facility
                                      </span>
                                      {hasCustomRates && (
                                        <button
                                          type="button"
                                          className="text-[11px] text-red-500 hover:text-red-700 flex items-center gap-1 hover:underline transition-colors"
                                          onClick={() => handleResetLocationRates(loc.id)}
                                        >
                                          <RotateCcw className="w-3 h-3" /> Reset to global
                                        </button>
                                      )}
                                    </div>
                                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                                      Overrides global rates for this facility. Leave empty to inherit global rate.
                                    </p>

                                    <div className="space-y-1.5 pt-1">
                                      {currentFormCodeTypes.map((codeTypeObj: any) => {
                                        const codeTypeName = typeof codeTypeObj === "object" && codeTypeObj !== null
                                          ? (codeTypeObj.name || codeTypeObj.code || String(codeTypeObj.id))
                                          : String(codeTypeObj);
                                        const codeTypeId = codeTypeObj?.id || codeTypeName;
                                        const globalVal = categoryRates[codeTypeName] ?? categoryRates[codeTypeId] ?? groupDefaultMedicareCharge;
                                        const locVal = locCustomRates[codeTypeName] ?? locCustomRates[codeTypeId];
                                        const isCustom = locVal !== undefined && locVal !== null && locVal !== "";

                                        return (
                                          <div
                                            key={codeTypeId}
                                            className={`flex items-center justify-between gap-2 p-1.5 px-2.5 rounded-lg border transition-all ${
                                              isCustom
                                                ? "bg-card border-primary/40 shadow-2xs"
                                                : "bg-muted/30 border-border/40"
                                            }`}
                                          >
                                            <div className="flex items-center gap-1.5 min-w-0">
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-bold bg-primary/10 text-primary border border-primary/20">
                                                {codeTypeName}
                                              </span>
                                              <span className="text-[10px] text-muted-foreground truncate">
                                                {isCustom ? "Custom override" : `Global: ${globalVal}%`}
                                              </span>
                                            </div>

                                            <div className="flex items-center gap-1 shrink-0">
                                              <div className="relative w-20">
                                                <Input
                                                  type="number"
                                                  min={0}
                                                  max={100}
                                                  placeholder={`${globalVal}`}
                                                  value={locVal !== undefined ? locVal : ""}
                                                  onChange={(e) => handleUpdateLocationRate(loc.id, codeTypeName, e.target.value)}
                                                  className="pr-5 h-7 text-xs font-semibold bg-background text-right rounded-md"
                                                />
                                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs font-semibold">%</span>
                                              </div>
                                              {isCustom && (
                                                <button
                                                  type="button"
                                                  className="text-muted-foreground/60 hover:text-red-500 p-1 transition-colors"
                                                  title="Reset this code to global rate"
                                                  onClick={() => handleRemoveLocationRate(loc.id, codeTypeName)}
                                                >
                                                  <X className="w-3.5 h-3.5" />
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                      {currentFormCodeTypes.length === 0 && (
                                        <div className="text-center p-2.5 border border-dashed rounded-lg text-xs text-muted-foreground bg-muted/10">
                                          Select at least one Billing Code Type above first.
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {serverError && (
                      <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md">
                        {serverError}
                      </div>
                    )}

                    <Button type="submit" className="w-full rounded-xl" disabled={inviteMutation.isPending}>
                      {inviteMutation.isPending ? "Sending..." : "Send Invitation"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Sent Invitations List */}
          {activeTab === 'team' && (
            <motion.div custom={3} variants={fadeUp} initial="hidden" animate="visible" className="lg:col-span-7 xl:col-span-8">
              <Card className="rounded-2xl shadow-xs border-border h-full">
                <CardHeader className="pb-4">
                  <CardTitle>Sent Invitations</CardTitle>
                  <CardDescription>Track the status of your team members.</CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <p>Loading...</p>
                  ) : invitations?.data && invitations.data.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead>NPI</TableHead>
                          <TableHead>Fee Schedule Rate (%)</TableHead>
                          <TableHead>Assigned Locations</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="w-[100px] text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {invitations.data.map((inv: any) => (
                          <TableRow key={inv.id}>
                            <TableCell>{inv.email}</TableCell>
                            <TableCell>{inv.npiNumber}</TableCell>
                            <TableCell>
                              {inv.categoryRates && Object.keys(inv.categoryRates).filter(k => k !== 'assignedLocationIds' && k !== 'assignedLocations' && !k.startsWith('loc_') && typeof inv.categoryRates[k] !== 'object').length > 0 ? (
                                <div className="flex flex-wrap gap-1 max-w-xs">
                                  {Object.entries(inv.categoryRates)
                                    .filter(([key, val]) => key !== 'assignedLocationIds' && key !== 'assignedLocations' && !key.startsWith('loc_') && typeof val !== 'object')
                                    .filter(([key], idx, arr) => arr.findIndex(([k]) => k.toLowerCase() === key.toLowerCase()) === idx)
                                    .map(([key, val]) => {
                                      const resolvedName = activeCodeTypes.find((ct: any) => String(ct.id) === key || ct.name?.toLowerCase() === key.toLowerCase() || ct.code?.toLowerCase() === key.toLowerCase())?.name || key;
                                      return (
                                       <span key={key} className="inline-flex items-center gap-1 font-bold text-[11px] px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800">
                                         {resolvedName}: {String(val).replace(/%/g, '')}%
                                       </span>
                                      );
                                    })
                                  }
                                </div>
                              ) : (
                                <span className="font-semibold text-xs">{String(inv.percentage ?? '').replace(/%/g, '')}%</span>
                              )}
                              {/* Location custom rates summary badge */}
                              {inv.categoryRates && Object.entries(inv.categoryRates)
                                .filter(([k, v]) => (k.startsWith('loc_') || providerLocations.some(l => l.id === k)) && typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length > 0)
                                .slice(0, 2)
                                .map(([k, locRatesObj]: [string, any]) => {
                                  const locName = providerLocations.find(l => l.id === k || `loc_${providerLocations.indexOf(l) + 1}` === k)?.name || k;
                                  return (
                                    <div key={k} className="mt-1">
                                      <span className="inline-flex items-center gap-1 font-semibold text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800">
                                        <MapPin className="w-2.5 h-2.5" />
                                        {locName}: {Object.entries(locRatesObj).map(([c, r]) => `${c} ${r}%`).join(', ')}
                                      </span>
                                    </div>
                                  );
                                })
                              }
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const invRates = inv.categoryRates || {};
                                const assignedLocs: string[] = invRates.assignedLocations || inv.assignedLocations || [];
                                const assignedLocIds: string[] = invRates.assignedLocationIds || inv.assignedLocationIds || [];
                                
                                let displayLocs: string[] = [];
                                if (assignedLocs.length > 0) {
                                  displayLocs = assignedLocs;
                                } else if (assignedLocIds.length > 0 && providerLocations.length > 0) {
                                  displayLocs = providerLocations.filter(l => assignedLocIds.includes(l.id)).map(l => l.name);
                                } else if (providerLocations.length > 0) {
                                  displayLocs = providerLocations.map(l => l.name);
                                }

                                if (displayLocs.length === 0) {
                                  return <span className="text-xs text-muted-foreground italic">All Practice Locations</span>;
                                }

                                return (
                                  <div className="flex flex-wrap gap-1 max-w-xs">
                                    {displayLocs.map((locName, i) => (
                                      <Badge key={i} variant="outline" className="text-[11px] font-semibold bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 gap-1">
                                        <MapPin className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                                        {locName}
                                      </Badge>
                                    ))}
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                inv.status === 'ACCEPTED' ? 'bg-green-100 text-green-800' : inv.status === 'REMOVED' ? 'bg-rose-100 text-rose-800' : inv.status === 'LEFT' ? 'bg-slate-100 text-slate-700' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {inv.status}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/40"
                                  title="Edit Code Types, % Rates & Locations"
                                  onClick={() => {
                                     setEditTarget(inv);
                                     setEditPercentage(inv.percentage !== undefined && inv.percentage !== null ? String(inv.percentage) : String(groupDefaultMedicareCharge));
                                     
                                     const editRates: Record<string, number | string> = {};
                                     const editSelected: Record<string, boolean> = {};
                                     const invRates = inv.categoryRates || {};
                                     const hasExistingRates = invRates && Object.keys(invRates).length > 0;

                                     displayedCodeTypesGroup.forEach((codeTypeObj: any) => {
                                       const codeTypeName = typeof codeTypeObj === "object" && codeTypeObj !== null
                                         ? (codeTypeObj.name || codeTypeObj.code || String(codeTypeObj.id))
                                         : String(codeTypeObj);
                                       const codeTypeId = codeTypeObj?.id || codeTypeName;

                                       const hasRate = invRates[codeTypeName] !== undefined || invRates[codeTypeId] !== undefined;
                                       editSelected[codeTypeName] = hasExistingRates ? hasRate : true;
                                       editSelected[codeTypeId] = hasExistingRates ? hasRate : true;

                                       const rateVal = invRates[codeTypeName] ?? invRates[codeTypeId] ?? groupCategoryRates[codeTypeName] ?? groupCategoryRates[codeTypeId] ?? groupDefaultMedicareCharge;
                                       editRates[codeTypeName] = rateVal;
                                       editRates[codeTypeId] = rateVal;
                                     });

                                     setEditCategoryRates(editRates);
                                     setEditSelectedCodeTypes(editSelected);

                                     // Populate edit location assignments
                                     const existingAssignedLocIds: string[] = invRates?.assignedLocationIds || inv?.assignedLocationIds || [];
                                     const editLocs: Record<string, boolean> = {};
                                     providerLocations.forEach((loc) => {
                                       if (existingAssignedLocIds.length > 0) {
                                         editLocs[loc.id] = existingAssignedLocIds.includes(loc.id);
                                       } else {
                                         editLocs[loc.id] = true;
                                       }
                                     });
                                     setEditAssignedLocationIds(editLocs);

                                     // Extract location overrides
                                     const initialEditLocationRates: Record<string, Record<string, number | string>> = {};
                                     providerLocations.forEach((loc, idx) => {
                                       const found = invRates[loc.id] || invRates[`loc_${idx + 1}`] || invRates[`loc_${idx}`];
                                       if (found && typeof found === 'object' && !Array.isArray(found)) {
                                         initialEditLocationRates[loc.id] = { ...found };
                                       }
                                     });
                                     setEditLocationRates(initialEditLocationRates);
                                     setEditActiveLocationId(null);

                                     setShowEditDialog(true);
                                   }}
                                >
                                   <Pencil className="w-4 h-4" />
                                 </Button>
                                 <AlertDialog>
                                   <AlertDialogTrigger asChild>
                                     <Button
                                       variant="ghost"
                                       size="icon"
                                       className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                                       title="Remove from team"
                                       disabled={deleteMutation.isPending}
                                     >
                                       <Trash2 className="w-4 h-4" />
                                     </Button>
                                   </AlertDialogTrigger>
                                   <AlertDialogContent>
                                     <AlertDialogHeader>
                                       <AlertDialogTitle>Remove Provider</AlertDialogTitle>
                                       <AlertDialogDescription>
                                         Are you sure you want to remove {inv.email} from your team? This action will disconnect the provider from active group management.
                                       </AlertDialogDescription>
                                     </AlertDialogHeader>
                                     <AlertDialogFooter>
                                       <AlertDialogCancel>Cancel</AlertDialogCancel>
                                       <AlertDialogAction onClick={() => handleDelete(inv.id)} className="bg-red-500 hover:bg-red-600 text-white">
                                         Remove
                                       </AlertDialogAction>
                                     </AlertDialogFooter>
                                   </AlertDialogContent>
                                 </AlertDialog>
                               </div>
                             </TableCell>
                          </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-16 text-muted-foreground border border-dashed rounded-2xl bg-muted/10 flex flex-col items-center justify-center">
                      <div className="w-12 h-12 bg-primary/5 rounded-full flex items-center justify-center mb-4">
                        <Users className="w-6 h-6 text-primary/40" />
                      </div>
                      <h3 className="font-semibold text-foreground mb-1">No team members yet</h3>
                      <p className="text-sm max-w-sm">Use the form on the left to invite providers to join your group practice.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Received Invitations List */}
          {activeTab === 'groups' && (
            <motion.div custom={4} variants={fadeUp} initial="hidden" animate="visible" className="lg:col-span-12">
              <Card className="rounded-2xl shadow-xs border-border h-full">
                <CardHeader className="pb-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                      <CardTitle>Invited & Accepted Groups</CardTitle>
                      <CardDescription className="mt-1">Groups you have been invited to or joined.</CardDescription>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                      <div className="relative w-full sm:w-64">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input 
                          placeholder="Search groups..." 
                          value={invitationSearch}
                          onChange={(e) => setInvitationSearch(e.target.value)}
                          className="pl-9 h-9 w-full rounded-xl text-sm bg-muted/30 focus:bg-background"
                        />
                      </div>
                      <Select value={invitationStatus} onValueChange={setInvitationStatus}>
                        <SelectTrigger className="w-full sm:w-32 h-9 rounded-xl text-sm bg-muted/30 focus:bg-background border-border">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ALL">All Status</SelectItem>
                          <SelectItem value="PENDING">Pending</SelectItem>
                          <SelectItem value="ACCEPTED">Accepted</SelectItem>
                          <SelectItem value="REJECTED">Rejected</SelectItem>
                          <SelectItem value="REMOVED">Removed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {isMyInvLoading ? (
                    <p>Loading...</p>
                  ) : filteredMyInvitations.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Group Name</TableHead>
                          <TableHead>Fee Schedule Rate (%)</TableHead>
                          <TableHead>Assigned Locations</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredMyInvitations.map((inv: any) => (
                          <TableRow key={inv.id}>
                            <TableCell>{inv.group?.user?.displayName || (inv.group?.user?.firstName ? `${inv.group.user.firstName} ${inv.group.user.lastName}` : "Group Practice")}</TableCell>
                            <TableCell>
                              {inv.categoryRates && Object.keys(inv.categoryRates).filter(k => k !== 'assignedLocationIds' && k !== 'assignedLocations' && !k.startsWith('loc_') && typeof inv.categoryRates[k] !== 'object').length > 0 ? (
                                <div className="flex flex-wrap gap-1 max-w-xs">
                                  {Object.entries(inv.categoryRates)
                                    .filter(([key, val]) => key !== 'assignedLocationIds' && key !== 'assignedLocations' && !key.startsWith('loc_') && typeof val !== 'object')
                                    .filter(([key], idx, arr) => arr.findIndex(([k]) => k.toLowerCase() === key.toLowerCase()) === idx)
                                    .map(([key, val]) => {
                                      const resolvedName = activeCodeTypes.find((ct: any) => String(ct.id) === key || ct.name?.toLowerCase() === key.toLowerCase() || ct.code?.toLowerCase() === key.toLowerCase())?.name || key;
                                      return (
                                       <span key={key} className="inline-flex items-center gap-1 font-bold text-[11px] px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800">
                                         {resolvedName}: {String(val).replace(/%/g, '')}%
                                       </span>
                                      );
                                    })
                                  }
                                </div>
                              ) : (
                                <span className="font-semibold text-xs">{String(inv.percentage ?? '').replace(/%/g, '')}%</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const invRates = inv.categoryRates || {};
                                const assignedLocs: string[] = invRates.assignedLocations || inv.assignedLocations || [];
                                if (assignedLocs.length === 0) {
                                  return <span className="text-xs text-muted-foreground italic">All Practice Locations</span>;
                                }
                                return (
                                  <div className="flex flex-wrap gap-1 max-w-xs">
                                    {assignedLocs.map((locName, i) => (
                                      <Badge key={i} variant="outline" className="text-[11px] font-semibold bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 gap-1">
                                        <MapPin className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                                        {locName}
                                      </Badge>
                                    ))}
                                  </div>
                                );
                              })()}
                            </TableCell>
                             <TableCell>
                               <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                 inv.status === 'ACCEPTED' ? 'bg-green-100 text-green-800' : inv.status === 'REJECTED' ? 'bg-red-100 text-red-800' : inv.status === 'REMOVED' ? 'bg-rose-100 text-rose-800' : 'bg-yellow-100 text-yellow-800'
                               }`}>
                                 {inv.status}
                               </span>
                             </TableCell>
                            <TableCell className="text-right">
                              {inv.status === 'PENDING' && (
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                    disabled={acceptMutation.isPending || rejectMutation.isPending}
                                    onClick={() => {
                                      acceptMutation.mutate(inv.id, {
                                        onSuccess: () => toast({ title: "Accepted", description: "You have joined the group practice." }),
                                        onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" })
                                      });
                                    }}
                                  >
                                    Accept
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    disabled={acceptMutation.isPending || rejectMutation.isPending}
                                    onClick={() => {
                                      rejectMutation.mutate(inv.id, {
                                        onSuccess: () => toast({ title: "Rejected", description: "You have declined the group practice invitation." }),
                                        onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" })
                                      });
                                    }}
                                  >
                                    Reject
                                  </Button>
                                </div>
                              )}
                              {inv.status === 'ACCEPTED' && (
                                <div className="flex justify-end items-center gap-2">
                                  <Dialog>
                                    <DialogTrigger asChild>
                                      <Button variant="outline" size="sm" className="h-8 text-xs border-primary/20 text-primary hover:bg-primary/5">
                                        View Report
                                      </Button>
                                    </DialogTrigger>
                                    <DialogContent className="max-w-5xl">
                                      <DialogHeader>
                                        <DialogTitle>Report for {inv.group?.user?.displayName || (inv.group?.user?.firstName ? `${inv.group.user.firstName} ${inv.group.user.lastName}` : "Group Practice")}</DialogTitle>
                                        <DialogDescription>
                                          Performance overview and patient statistics for your practice.
                                        </DialogDescription>
                                      </DialogHeader>
                                      <div className="space-y-6 pt-4">
                                        <div className="flex justify-between items-center">
                                          <h3 className="text-lg font-medium">Overview</h3>
                                          <select className="border-border rounded-md px-3 py-1.5 text-sm bg-background">
                                            <option value="today">Today</option>
                                            <option value="yesterday">Yesterday</option>
                                            <option value="this_week">This Week</option>
                                            <option value="last_week">Last Week</option>
                                            <option value="this_month">This Month</option>
                                            <option value="last_month">Last Month</option>
                                            <option value="this_year">This Year</option>
                                            <option value="all_time">All Time</option>
                                          </select>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                          <div className="p-4 rounded-xl bg-primary/5 border border-primary/10">
                                            <p className="text-sm text-muted-foreground mb-1">Total Revenue</p>
                                            <p className="text-3xl font-bold text-primary">${realTotalRevenue.toLocaleString()}</p>
                                          </div>
                                          <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/20">
                                            <p className="text-sm text-muted-foreground mb-1">Total Patients</p>
                                            <p className="text-3xl font-bold text-blue-600">{realTotalPatients}</p>
                                          </div>
                                        </div>
                                        <div className="mt-6 border border-border rounded-xl overflow-hidden">
                                          <Table>
                                            <TableHeader className="bg-muted/30">
                                              <TableRow>
                                                <TableHead>Patient Name</TableHead>
                                                <TableHead>Date</TableHead>
                                                <TableHead>Service</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead className="text-right">Revenue</TableHead>
                                              </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                              {realPatientsList.length > 0 ? (
                                                realPatientsList.map((patient: any) => (
                                                  <TableRow key={patient.id}>
                                                    <TableCell className="font-medium">{patient.name}</TableCell>
                                                    <TableCell>{patient.date}</TableCell>
                                                    <TableCell>{patient.service}</TableCell>
                                                    <TableCell>
                                                      <span className={`px-2 py-1 rounded-full text-[10px] font-semibold ${patient.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : patient.status === 'APPROVED' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                        {patient.status}
                                                      </span>
                                                    </TableCell>
                                                    <TableCell className="text-right font-medium text-green-600">{patient.revenue}</TableCell>
                                                  </TableRow>
                                                ))
                                              ) : (
                                                <TableRow>
                                                  <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                                                    No appointments found.
                                                  </TableCell>
                                                </TableRow>
                                              )}
                                            </TableBody>
                                          </Table>
                                        </div>
                                      </div>
                                    </DialogContent>
                                  </Dialog>

                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="outline" size="sm" className="h-8 text-xs border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-900 dark:hover:bg-red-950/40">
                                        Leave Group
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Leave Group Practice?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Are you sure you want to leave {inv.group?.user?.displayName || "this group practice"}? You will no longer have access to group appointments or group data. Historical group records will remain attached to the group profile.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                          className="bg-red-600 hover:bg-red-700 text-white"
                                          disabled={leaveGroupMutation.isPending}
                                          onClick={() => {
                                            leaveGroupMutation.mutate(inv.id, {
                                              onSuccess: () => {
                                                toast({ title: "Left Group", description: "You have successfully left the group practice." });
                                                refetch();
                                              },
                                              onError: (err: any) => {
                                                toast({ title: "Error", description: err.message, variant: "destructive" });
                                              }
                                            });
                                          }}
                                        >
                                          {leaveGroupMutation.isPending ? "Leaving..." : "Confirm & Leave"}
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground border border-dashed rounded-2xl bg-muted/10 flex flex-col items-center justify-center">
                      <p className="text-sm">You have no pending invitations to join a group practice.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      </div>

      {/* Edit Fee Schedule & Locations Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => { setShowEditDialog(open); if (!open) setEditActiveLocationId(null); }}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Percent className="w-5 h-5 text-indigo-600" /> Edit Fee Schedule & Locations
            </DialogTitle>
            <DialogDescription>
              Update Fee Schedule rates and assigned locations for <strong>{editTarget?.email}</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div>
              <Label className="text-xs font-semibold text-slate-500">Provider Email</Label>
              <Input value={editTarget?.email || ""} disabled className="mt-1 bg-slate-50 dark:bg-slate-900 rounded-xl" />
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-500">NPI Number</Label>
              <Input value={editTarget?.npiNumber || "-"} disabled className="mt-1 bg-slate-50 dark:bg-slate-900 rounded-xl" />
            </div>

            {/* Code Type Rates Edit */}
            <div className="space-y-2.5 pt-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Percent className="w-3.5 h-3.5 text-primary" /> Billing Code Types & Rates (% of Fee Schedule)
                </Label>
              </div>
              <p className="text-[11px] text-muted-foreground">Select applicable billing code types and set reimbursement % rates.</p>

              {/* Clean Code Types Table */}
              <div className="rounded-xl border border-border/80 bg-card overflow-hidden divide-y divide-border/60">
                <div className="flex items-center justify-between px-3 py-2 bg-muted/40 text-[11px] font-semibold text-muted-foreground">
                  <span>Code Type</span>
                  <span>Reimbursement Rate</span>
                </div>

                <div className="max-h-56 overflow-y-auto divide-y divide-border/50">
                  {displayedCodeTypesGroup.map((codeTypeObj: any, idx: number) => {
                    const codeTypeName = typeof codeTypeObj === "object" && codeTypeObj !== null
                      ? (codeTypeObj.name || codeTypeObj.code || String(codeTypeObj.id || idx))
                      : String(codeTypeObj || `CodeType-${idx}`);
                    const codeTypeId = codeTypeObj?.id || codeTypeName;
                    const isSelected = editSelectedCodeTypes[codeTypeName] === true || editSelectedCodeTypes[codeTypeId] === true;
                    const currentVal = editCategoryRates[codeTypeName] ?? editCategoryRates[codeTypeId] ?? groupDefaultMedicareCharge;
                    const defaultForType = groupCategoryRates[codeTypeName] ?? groupCategoryRates[codeTypeId] ?? groupDefaultMedicareCharge;
                    const isCustomCode = customCodeTypesList.includes(codeTypeName);

                    return (
                      <div
                        key={codeTypeId}
                        className={`flex items-center justify-between gap-3 px-3 py-2 transition-colors ${
                          isSelected ? "bg-card" : "bg-muted/10 opacity-60"
                        }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Checkbox
                            id={`edit-code-${codeTypeId}`}
                            checked={isSelected}
                            onCheckedChange={(checked) => {
                              const isChecked = Boolean(checked);
                              setEditSelectedCodeTypes(prev => ({
                                ...prev,
                                [codeTypeName]: isChecked,
                                [codeTypeId]: isChecked,
                              }));
                              if (isChecked && (editCategoryRates[codeTypeName] === undefined || editCategoryRates[codeTypeName] === "")) {
                                setEditCategoryRates(prev => ({
                                  ...prev,
                                  [codeTypeName]: defaultForType,
                                  [codeTypeId]: defaultForType,
                                }));
                              }
                            }}
                          />
                          <label
                            htmlFor={`edit-code-${codeTypeId}`}
                            className="flex items-center gap-1.5 cursor-pointer select-none"
                          >
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold transition-colors ${
                              isSelected
                                ? "bg-primary/10 text-primary border border-primary/25"
                                : "bg-muted text-muted-foreground border border-border"
                            }`}>
                              {String(codeTypeName)}
                            </span>
                          </label>
                          {isCustomCode && (
                            <button
                              type="button"
                              onClick={() => {
                                setCustomCodeTypesList(prev => prev.filter(c => c !== codeTypeName));
                                setEditSelectedCodeTypes(prev => ({ ...prev, [codeTypeName]: false, [codeTypeId]: false }));
                              }}
                              className="text-muted-foreground/50 hover:text-red-500 text-xs transition-colors p-0.5"
                              title="Remove custom code type"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <div className="relative w-20 sm:w-24">
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              disabled={!isSelected}
                              placeholder={`${defaultForType}`}
                              value={isSelected ? currentVal : ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                setEditCategoryRates(prev => ({
                                  ...prev,
                                  [codeTypeName]: val,
                                  [codeTypeId]: val
                                }));
                              }}
                              className={`pr-6 h-8 text-xs font-semibold text-right rounded-lg transition-all ${
                                isSelected
                                  ? "bg-background border-border font-bold shadow-2xs"
                                  : "bg-muted/40 text-muted-foreground border-transparent cursor-not-allowed"
                              }`}
                            />
                            <span className={`absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-semibold ${
                              isSelected ? "text-muted-foreground" : "text-muted-foreground/40"
                            }`}>%</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Subtle Add Custom Code Action for Edit Dialog */}
              {!showEditAddCustomCode ? (
                <button
                  type="button"
                  onClick={() => setShowEditAddCustomCode(true)}
                  className="text-xs text-primary hover:underline font-medium flex items-center gap-1 pt-0.5"
                >
                  <Plus className="w-3.5 h-3.5" /> Add custom code type
                </button>
              ) : (
                <div className="flex items-center gap-2 p-2 rounded-xl bg-muted/30 border border-dashed border-primary/30 mt-1">
                  <Input
                    placeholder="Code name (e.g. LAB)"
                    value={editCustomCodeName}
                    onChange={(e) => setEditCustomCodeName(e.target.value.toUpperCase())}
                    className="h-8 text-xs bg-background uppercase font-bold"
                    autoFocus
                  />
                  <div className="relative w-20 shrink-0">
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      placeholder={`${groupDefaultMedicareCharge}`}
                      value={editCustomCodeRate}
                      onChange={(e) => setEditCustomCodeRate(e.target.value)}
                      className="pr-6 h-8 text-xs font-semibold text-right bg-background"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs font-semibold">%</span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 px-2.5 text-xs font-semibold shrink-0"
                    disabled={!editCustomCodeName.trim()}
                    onClick={handleEditAddCustomCode}
                  >
                    Add
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-xs text-muted-foreground shrink-0"
                    onClick={() => {
                      setShowEditAddCustomCode(false);
                      setEditCustomCodeName("");
                      setEditCustomCodeRate("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>

            {/* Facility Locations Edit */}
            {providerLocations.length > 0 && (
              <div className="space-y-2.5 pt-2 border-t border-border">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-primary" /> Assigned Practice Locations
                  </Label>
                  {providerLocations.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const allAssigned = providerLocations.every(l => editAssignedLocationIds[l.id] !== false);
                        const next: Record<string, boolean> = {};
                        providerLocations.forEach(l => { next[l.id] = !allAssigned; });
                        setEditAssignedLocationIds(next);
                        if (allAssigned) setEditActiveLocationId(null);
                      }}
                      className="text-[11px] text-primary hover:underline font-medium"
                    >
                      {providerLocations.every(l => editAssignedLocationIds[l.id] !== false) ? "Deselect All" : "Select All"}
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground">Select provider locations. Custom rates can optionally be configured per facility.</p>
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {providerLocations.map((loc) => {
                    const isSelected = editAssignedLocationIds[loc.id] !== false;
                    const locCustomRates = editLocationRates[loc.id] || {};
                    const customRatesCount = Object.keys(locCustomRates).filter(k => locCustomRates[k] !== "" && locCustomRates[k] !== undefined && locCustomRates[k] !== null).length;
                    const hasCustomRates = customRatesCount > 0;
                    const isExpanded = editActiveLocationId === loc.id;

                    const currentEditCodeTypes = displayedCodeTypesGroup.filter(
                      (ct: any) => editSelectedCodeTypes[ct.name] === true || editSelectedCodeTypes[ct.id] === true
                    );

                    return (
                      <div
                        key={loc.id}
                        className={`border rounded-xl transition-all overflow-hidden ${
                          isSelected
                            ? "bg-card border-border/80 shadow-2xs"
                            : "bg-muted/10 border-border/50 opacity-60"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3 p-2.5 px-3">
                          <div className="flex items-center gap-2.5 min-w-0 flex-1">
                            <Checkbox
                              id={`edit-loc-${loc.id}`}
                              checked={isSelected}
                              onCheckedChange={(checked) => {
                                const val = Boolean(checked);
                                setEditAssignedLocationIds(prev => ({
                                  ...prev,
                                  [loc.id]: val
                                }));
                                if (!val && editActiveLocationId === loc.id) {
                                  setEditActiveLocationId(null);
                                }
                              }}
                            />
                            <label htmlFor={`edit-loc-${loc.id}`} className="text-xs font-medium text-foreground cursor-pointer select-none truncate">
                              {loc.name}
                            </label>
                          </div>

                          {isSelected && (
                            <div className="flex items-center gap-1.5 shrink-0">
                              {hasCustomRates && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary border-primary/20 font-semibold flex items-center gap-1">
                                  <Sliders className="w-2.5 h-2.5" />
                                  <span>{customRatesCount} custom</span>
                                </Badge>
                              )}
                              <button
                                type="button"
                                className={`text-[11px] font-medium px-2 py-1 rounded-md flex items-center gap-1 transition-colors ${
                                  isExpanded
                                    ? "bg-primary/10 text-primary font-semibold"
                                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                                }`}
                                title="Configure location-specific fee schedule rates"
                                onClick={() => setEditActiveLocationId(prev => prev === loc.id ? null : loc.id)}
                              >
                                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                <span>{isExpanded ? "Close" : hasCustomRates ? "Edit rates" : "Custom rates"}</span>
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Location-specific Fee Schedule rates editor panel in Dialog */}
                        {isSelected && isExpanded && (
                          <div className="p-3 bg-muted/20 border-t border-border/70 space-y-2.5">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                                <MapPin className="w-3.5 h-3.5 text-primary" />
                                Custom Rates for this Facility
                              </span>
                              {hasCustomRates && (
                                <button
                                  type="button"
                                  className="text-[11px] text-red-500 hover:text-red-700 flex items-center gap-1 hover:underline transition-colors"
                                  onClick={() => handleResetEditLocationRates(loc.id)}
                                >
                                  <RotateCcw className="w-3 h-3" /> Reset to global
                                </button>
                              )}
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-relaxed">
                              Overrides global rates for this facility. Leave empty to inherit global rate.
                            </p>

                            <div className="space-y-1.5 pt-1">
                              {currentEditCodeTypes.map((codeTypeObj: any) => {
                                const codeTypeName = typeof codeTypeObj === "object" && codeTypeObj !== null
                                  ? (codeTypeObj.name || codeTypeObj.code || String(codeTypeObj.id))
                                  : String(codeTypeObj);
                                const codeTypeId = codeTypeObj?.id || codeTypeName;
                                const globalVal = editCategoryRates[codeTypeName] ?? editCategoryRates[codeTypeId] ?? groupDefaultMedicareCharge;
                                const locVal = locCustomRates[codeTypeName] ?? locCustomRates[codeTypeId];
                                const isCustom = locVal !== undefined && locVal !== null && locVal !== "";

                                return (
                                  <div
                                    key={codeTypeId}
                                    className={`flex items-center justify-between gap-2 p-1.5 px-2.5 rounded-lg border transition-all ${
                                      isCustom
                                        ? "bg-card border-primary/40 shadow-2xs"
                                        : "bg-muted/30 border-border/40"
                                    }`}
                                  >
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-bold bg-primary/10 text-primary border border-primary/20">
                                        {codeTypeName}
                                      </span>
                                      <span className="text-[10px] text-muted-foreground truncate">
                                        {isCustom ? "Custom override" : `Global: ${globalVal}%`}
                                      </span>
                                    </div>

                                    <div className="flex items-center gap-1 shrink-0">
                                      <div className="relative w-20">
                                        <Input
                                          type="number"
                                          min={0}
                                          max={100}
                                          placeholder={`${globalVal}`}
                                          value={locVal !== undefined ? locVal : ""}
                                          onChange={(e) => handleUpdateEditLocationRate(loc.id, codeTypeName, e.target.value)}
                                          className="pr-5 h-7 text-xs font-semibold bg-background text-right rounded-md"
                                        />
                                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs font-semibold">%</span>
                                      </div>
                                      {isCustom && (
                                        <button
                                          type="button"
                                          className="text-muted-foreground/60 hover:text-red-500 p-1 transition-colors"
                                          title="Reset this code to global rate"
                                          onClick={() => handleRemoveEditLocationRate(loc.id, codeTypeName)}
                                        >
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                              {currentEditCodeTypes.length === 0 && (
                                <div className="text-center p-2.5 border border-dashed rounded-lg text-xs text-muted-foreground bg-muted/10">
                                  Select at least one Billing Code Type above first.
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold px-5"
              disabled={updateMutation.isPending}
              onClick={handleSavePercentage}
            >
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </ProviderLayout>
  );
}
